/* eslint-disable consistent-return */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Grid, InputAdornment, IconButton, CircularProgress, Typography } from '@mui/material';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import { Formik } from 'formik';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { decode } from 'html-entities';
import { isEmpty } from 'modules/common/helpers/object';
import ERROR_TYPES from 'modules/common/constants/error-types';
import ActionButton from 'modules/common/components/action-button';
import { Alert, TextField } from 'modules/common/components/add-hotel-group/style';
import { LABELS } from 'modules/common/components/add-hotel-group/constants';
import { dashboardActions } from 'modules/dashboard/slice';
import { selectHotelGroupQuery, selectLLMRequestState } from 'modules/dashboard/selectors';
import { dataHandler } from '@phg/advanced-querying-module';
import queryBoxValidation from '../../validation/query-validation';
import { selectSchemaMapping } from '../../../../auth/selectors';

/** Implementation to categorize Group A and Group B based on user input query
 * @param {Array} breakdownSubCategories - List of breakdown sub categories
 * @param {Function} setGroup1 - Function to set Group A values
 * @param {Function} setGroup2 - Function to set Group B values
 * @param {Function} onClose - Function to trigger on close event
 * @param {Boolean} open - To open/ close dialog box
 * @param {Boolean} isQueryEnabled - To enable/disable advanced querying feature
 * @param {Boolean} loadFilter - To load saved filter
 * @param {Function} setLoadFilter  - Function to set load filter true when the saved filter is clicked to be loaded
 * @returns
 */

const QueryBox = ({
  setGroupList,
  open,
  isQueryEnabled,
  loadFilter,
  setLoadFilter,
  focusOnList,
  setTemGroupList,
  previewButtonRef,
  filterError,
  filterErrorMessage,
  isQueryEnabledErrorMessage,
}) => {
  const dispatch = useDispatch();
  // setA Query and set B Query values are selected from store here. Refer each selector function for details
  const hotelGroupQuery = useSelector(selectHotelGroupQuery);
  // This flag is set by the Generate button in LLM input component
  const isHotelGroupQueryGenerating = useSelector(selectLLMRequestState);
  const hotelMappingSchema = useSelector(selectSchemaMapping);
  //
  const [hotelGroupNotification, setHotelGroupNotification] = useState({ type: '', message: '' });
  // timed "strategy" steps (generic; later we can swap to reasoning summaries)
  const TIMED_STEPS = [
    { label: 'Thinking', duration: 3000 },
    { label: 'Generating query', duration: 2000 },
    { label: 'Parsing', duration: 3000 },
    { label: 'Evaluating result', duration: 2000 },
  ];
  // local UI state for the timed steps
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [currentStepLabel, setCurrentStepLabel] = useState('');

  /**
   * START/STOP comes from the store (Generate button).
   * When generation starts => begin timed steps.
   * When generation stops => clear the steps immediately.
   */
  useEffect(() => {
    if (isHotelGroupQueryGenerating) {
      // start the timed sequence
      setIsProcessing(true);
      setCurrentStepIndex(0);
      setHotelGroupNotification({ type: '', message: '' });
    } else {
      // stop / reset when the external flag goes false
      setIsProcessing(false);
      setCurrentStepIndex(-1);
      setCurrentStepLabel('');
    }
  }, [isHotelGroupQueryGenerating]);
  /**
   * Drive the timed steps sequence.
   * This is purely UI; it does NOT control the backend call.
   * If the backend finishes early and turns isHotelGroupQueryGenerating=false,
   * the effect above will cancel this sequence.
   */
  useEffect(() => {
    if (!isProcessing || currentStepIndex < 0) return;

    const isLastStep = currentStepIndex === TIMED_STEPS.length - 1;

    // Always set current label
    setCurrentStepLabel(TIMED_STEPS[currentStepIndex].label);

    // If we are at the final step:
    if (isLastStep) {
      // Keep showing last step UNTIL backend finishes
      if (!isHotelGroupQueryGenerating) {
        // backend finished and stop the animation
        setIsProcessing(false);
        setCurrentStepIndex(-1);
        setCurrentStepLabel('');
      }

      // Do NOT auto-advance anymore
      return;
    }

    // Normal timed auto-advance for first 3 steps
    const timeoutId = setTimeout(() => {
      setCurrentStepIndex((prev) => prev + 1);
    }, TIMED_STEPS[currentStepIndex].duration);

    return () => clearTimeout(timeoutId);
  }, [isProcessing, currentStepIndex, isHotelGroupQueryGenerating]);

  // To preview values for Group A and B based on the input query
  const previewValues = (setAQuery = hotelGroupQuery) => {
    let setGroup = [];
    setGroupList([]);
    //
    const filteredList = focusOnList.map((obj) => {
      const { hotelClassification, ...rest } = obj;
      return rest;
    });
    //
    if (setAQuery) {
      try {
        setGroup = dataHandler(decode(setAQuery), filteredList, hotelMappingSchema);
        if (!isEmpty(setGroup)) {
          setGroupList(setGroup);
          setTemGroupList(setGroup);
          setHotelGroupNotification({ type: '', message: '' });
        } else {
          setTemGroupList([]);
          setHotelGroupNotification({ type: 'error', message: 'No match found for Hotel Group' });
        }
      } catch (e) {
        setTemGroupList([]);
        setHotelGroupNotification({ type: 'error', message: e.message });
      }
    }
    setLoadFilter(false);
  };
  // Triggered when load filter value changes
  useEffect(() => {
    // Call preview values function if loadFilter is  true to display selected filter values in transfer list
    if (loadFilter) previewValues();
  }, [loadFilter]);
  //
  return (
    open && (
      <Formik
        initialValues={{
          setHotelGroup: hotelGroupQuery,
        }}
        validationSchema={queryBoxValidation}
        onSubmit={() => previewValues()}
        enableReinitialize
      >
        {({ errors, handleBlur, handleSubmit, setFieldValue, touched, resetForm }) => (
          <form noValidate onSubmit={handleSubmit}>
            {hotelGroupNotification?.type === ERROR_TYPES.ERROR &&
              hotelGroupNotification?.message && (
                <Alert mt={2} mb={3} severity={hotelGroupNotification?.type}>
                  {hotelGroupNotification?.message}
                </Alert>
              )}
            <TextField
              multiline
              name="setHotelGroup"
              value={hotelGroupQuery}
              error={Boolean(touched.setHotelGroup && errors.setHotelGroup)}
              helperText={touched.setHotelGroup && errors.setHotelGroup}
              fullWidth
              onBlur={handleBlur}
              onChange={(event) => {
                setFieldValue('setHotelGroup', event.target.value);
                dispatch(dashboardActions.setHotelGroupQuery(event.target.value));
                dispatch(dashboardActions.setNaturalLanguageQuery(''));
                dispatch(dashboardActions.setRestLLMRequestState(false));
              }}
              my={2}
              label={LABELS.GROUP_HOTEL}
              size="small"
              disabled={!isQueryEnabled}
              sx={{
                '& .MuiOutlinedInput-root': {
                  padding: 2,
                  height: 'fit-content',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear text"
                      size="small"
                      edge="end"
                      disabled={!isQueryEnabled}
                      onMouseDown={(e) => e.preventDefault()} // keep focus in the field
                      onClick={() => {
                        resetForm();
                        dispatch(dashboardActions.setHotelGroupQuery(''));
                        dispatch(dashboardActions.setNaturalLanguageQuery(''));
                        dispatch(dashboardActions.setRestLLMRequestState(false));
                      }}
                    >
                      <ClearRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {/* Reasoning / intermediate steps status – driven by Generate flow */}
            {isProcessing && (
              <Grid container alignItems="center" justifyContent="flex-start" mt={1} mb={1}>
                <CircularProgress size={16} style={{ marginRight: 8 }} />
                <Typography variant="caption" color="text.secondary">
                  {currentStepLabel}...
                </Typography>
              </Grid>
            )}
            <Grid
              container
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '100%' }}
            >
              <Grid item>
                {filterError && (
                  <Alert
                    severity={ERROR_TYPES.ERROR}
                    sx={{
                      p: '2px 4px',
                      fontSize: '0.65rem',
                      alignItems: 'center',
                      maxWidth: 'fit-content',
                    }}
                  >
                    {filterErrorMessage}
                  </Alert>
                )}
                {isQueryEnabledErrorMessage && (
                  <Alert
                    severity={ERROR_TYPES.ERROR}
                    sx={{
                      p: '2px 4px',
                      fontSize: '0.65rem',
                      alignItems: 'center',
                      maxWidth: 'fit-content',
                    }}
                  >
                    {isQueryEnabledErrorMessage}
                  </Alert>
                )}
              </Grid>
              <Grid item>
                <ActionButton
                  ref={previewButtonRef}
                  type="submit"
                  label="Preview"
                  disabled={!isQueryEnabled}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    )
  );
};
//
export default QueryBox;
